import { useMutation } from 'react-query';
import { toast } from 'sonner';

import { APIBaseChronos } from '@/api/hosts';
import useGetFetchConfig, { FetchMethod } from '@/api/useGetFetchConfig';

interface Props {
  matterId: string;
  subsetId: string;
}

export const useDownloadDocumentsList = ({ matterId, subsetId }: Props) => {
  const { getFetchConfig } = useGetFetchConfig();

  const downloadDocumentsList = async ({
    format,
    title,
    columns,
    relativityColumns,
    searchQuery,
    processed,
    extensions,
    startDate,
    endDate,
    documentTypes,
    documentThemes,
    hasKeyFacts,
    hasRelevantFacts,
  }: {
    format: string;
    title: string;
    columns: string[];
    relativityColumns?: string[];
    searchQuery?: string;
    processed?: boolean;
    extensions?: string[];
    startDate?: string;
    endDate?: string;
    documentTypes?: string[];
    documentThemes?: string[];
    hasKeyFacts?: boolean;
    hasRelevantFacts?: boolean;
  }) => {
    const fetchConfig = getFetchConfig({
      method: FetchMethod.POST,
      data: {
        format,
        title,
        columns,
        relativityColumns,
        searchQuery,
        processed,
        extensions,
        startDate,
        endDate,
        documentTypes,
        documentThemes,
        hasKeyFacts,
        hasRelevantFacts,
      },
    });

    const response = await fetch(
      `${APIBaseChronos}/api/matter/${matterId}/subset/${subsetId}/docs/download-list/`,
      fetchConfig,
    );

    if (!response.ok) {
      throw new Error('Failed to download document list');
    }

    const blob = await response.blob();

    const link = Object.assign(document.createElement('a'), {
      href: URL.createObjectURL(blob),
      download: `${title}.${format}`,
    });

    link.click();

    URL.revokeObjectURL(link.href);
  };

  const { mutate: fetchDownload, isLoading: isLoadingDownload } = useMutation(downloadDocumentsList, {
    onError: () => {
      toast.error('Failed to download document list. Please try again.');
    },
  });

  return { fetchDownload, isLoadingDownload };
};
